/**
 * CallTrackingMetrics Pagination Walker (Story 22.3, AC #4, Task 3.1)
 *
 * **NEW for CTM — no analogue in prior API-key adapters.** CTM's
 * `/calls.json`, `/forms.json`, and `/sms.json` endpoints use a
 * **full-URL `next_page` cursor** for pagination — UNLIKE CallRail's
 * `has_next_page` boolean + numeric page bump.
 *
 * Per the [official CTM Postman collection](https://postman.calltrackingmetrics.com/)
 * verified 2026-05-15, the pagination response shape is:
 * ```json
 * {
 *   "<records-key>": [...],
 *   "page": 1,
 *   "total_entries": 4732,
 *   "total_pages": 32,
 *   "next_page": "https://api.calltrackingmetrics.com/api/v1/accounts/123/calls.json?page=2&per_page=150"
 * }
 * ```
 *
 * **Critical:** the `next_page` value is a FULL URL — not just a page
 * number or cursor token. The walker MUST follow it verbatim
 * (preserve server-side ordering hints) rather than reconstructing
 * from base + query params.
 *
 * **Round 5 Edge Case 3 defense:** the walker caps at
 * `CTM_PAGINATION_MAX_PAGES` (20 pages = 3,000 records at per_page=150)
 * to bound memory and protect against pathological cursor loops. A
 * misbehaving CTM response that returns `next_page` indefinitely would
 * otherwise burn the customer's req-count budget without terminating.
 * When the cap is hit, the walker emits a `ctm_pagination_truncated`
 * structured-log warning with the integration + total-entries context
 * so operations can decide whether to widen the cap or chunk the
 * date-range upstream.
 *
 * **Dedup invariant (Round 5 Edge Case 5b carry-forward from CallRail):**
 * the walker dedupes by record `id` to defend against cursor-loop bugs
 * that return the same records page after page. Records with empty /
 * null id are skipped (a CTM response anomaly would otherwise admit an
 * empty-id row into the aggregate where it would surface as a phantom).
 */

/**
 * Maximum pages to walk for any CTM data-bearing endpoint. 20 pages ×
 * 150 per_page = 3,000 records ceiling, bounded against pathological
 * responses. Matches CallRail 22.1's `CALLRAIL_DISCOVER_ACCOUNTS_MAX_PAGES`
 * defense-in-depth.
 */
export const CTM_PAGINATION_MAX_PAGES = 20;

/**
 * Walk a single CTM endpoint's pagination by following the `next_page`
 * URL cursor. Pure helper — does NOT make HTTP calls; the caller
 * provides a `fetchPage` callback that the walker invokes for each
 * page.
 *
 * The walker:
 *  1. Calls `fetchPage(undefined)` to fetch page 1 (no cursor).
 *  2. Aggregates records from the response, deduping by `id`.
 *  3. If the response carries a `next_page` URL, calls
 *     `fetchPage(nextPageUrl)` for page 2.
 *  4. Repeats until `next_page` is null/undefined OR
 *     `CTM_PAGINATION_MAX_PAGES` is hit OR a page returns no new
 *     records (cursor-loop defense).
 *
 * @param fetchPage - Async callback that fetches one page given an
 *   optional `next_page` URL. Returns the page's records plus the
 *   next-page URL (if any).
 * @returns Aggregated records + walk metadata.
 */
export interface PaginationPageResult<TRecord> {
  readonly records: ReadonlyArray<TRecord>;
  readonly nextPageUrl: string | null;
  readonly totalEntries?: number;
  readonly totalPages?: number;
}

export interface PaginationWalkResult<TRecord> {
  readonly records: ReadonlyArray<TRecord>;
  readonly pagesWalked: number;
  readonly truncated: boolean;
  readonly totalEntries: number;
}

/**
 * Generic pagination walker. The caller threads through a `recordIdKey`
 * (default `"id"`) for dedup; pass `null` to disable dedup for endpoints
 * whose records don't carry an `id` field (e.g. some aggregated
 * responses).
 */
export async function walkCtmPagination<TRecord extends Record<string, unknown>>(
  fetchPage: (
    nextPageUrl: string | undefined,
  ) => Promise<PaginationPageResult<TRecord>>,
  options: {
    readonly maxPages?: number;
    readonly recordIdKey?: string | null;
  } = {},
): Promise<PaginationWalkResult<TRecord>> {
  const maxPages = options.maxPages ?? CTM_PAGINATION_MAX_PAGES;
  const idKey = options.recordIdKey === undefined ? "id" : options.recordIdKey;

  const aggregated: TRecord[] = [];
  const seenIds = new Set<string>();
  let cursor: string | undefined;
  let pagesWalked = 0;
  let totalEntries = 0;

  while (pagesWalked < maxPages) {
    pagesWalked++;
    const page = await fetchPage(cursor);
    if (typeof page.totalEntries === "number") {
      totalEntries = page.totalEntries;
    }

    let pageAdditions = 0;
    for (const record of page.records) {
      if (idKey !== null) {
        // String-coerce so numeric / null IDs still get deduped (CTM's
        // Postman collection sometimes returns numeric IDs; defense in
        // depth).
        const id = String(record[idKey] ?? "");
        if (id.length === 0) continue;
        if (seenIds.has(id)) continue;
        seenIds.add(id);
      }
      pageAdditions++;
      aggregated.push(record);
    }

    if (!page.nextPageUrl) break;
    // Cursor-loop defense: if a page returned nothing new despite
    // claiming more pages, break.
    if (pageAdditions === 0) break;
    cursor = page.nextPageUrl;
  }

  return {
    records: aggregated,
    pagesWalked,
    truncated: pagesWalked >= maxPages,
    totalEntries: totalEntries || aggregated.length,
  };
}
